import { filter, isFunction, isPlainObject, isString, orderBy } from 'lodash';
import { TBaseTagContext } from '../types';
import { TTag } from './types';
import React, { useEffect, useMemo, useState } from 'react';
import UniversalChildren from '../../UniversalTag/UniversalChildren';
import { validate } from '@krugom/validator';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { selectUnits } from '../../../store/selector/selectUnits';
import { TBaseDataObject } from '../../../types';
import { filtersFunction } from './filtersFinction';
import { TValidationScheme } from '@krugom/validator/dist/types';

const validationScheme: TValidationScheme = {
  type: 'object',
  properties: {
    type: { type: 'string', required: true },
    sorts: {
      type: 'custom',
      validator: (v: unknown) =>
        isPlainObject(v) &&
        !Object.values(v as object).find(item => item !== 'asc' && item !== 'desc'),
    },
    filters: {
      type: 'custom',
      validator: (v: unknown) => isPlainObject(v) || isFunction(v) || isString(v),
    },
    pagination: {
      type: 'object',
      properties: {
        pageSize: { type: 'number', required: true },
        start: { type: 'number' },
        end: { type: 'number' },
      },
    },
  },
};

export const CustomCollection: React.FC<{
  tagProps: TTag;
  context: Partial<TBaseTagContext>;
}> = React.memo(({ tagProps, context }) => {
  const result = validate(tagProps, validationScheme);

  const [filters, setFilters] = useState<TTag['filters']>(tagProps.filters);
  const [sorts, setSorts] = useState<TTag['sorts']>(tagProps.sorts);
  const defaultPagination =
    tagProps.pagination?.page && tagProps.pagination?.pageSize
      ? { page: tagProps.pagination.page || 1, pageSize: tagProps.pagination.pageSize || 25 }
      : undefined;
  const [pagination, setPagination] = useState<{ page: number; pageSize: number } | undefined>(
    defaultPagination
  );

  useEffect(() => {
    setFilters(tagProps.filters);
    setSorts(tagProps.sorts);
    setPagination(defaultPagination);
  }, [tagProps, defaultPagination]);

  const selector = useSelector((state: RootState) => selectUnits(state, tagProps.type));
  const items = useMemo(() => {
    const srcItems: TBaseDataObject[] = Object.values(selector || {});
    const collectionFilters = filters
      ? isString(filters)
        ? filtersFunction(filters)
        : filters
      : undefined;
    const filteredItems = collectionFilters
      ? (filter(srcItems, collectionFilters) as TBaseDataObject[])
      : srcItems;
    const sortedItems = sorts
      ? (orderBy(filteredItems, [Object.keys(sorts), Object.values(sorts)]) as TBaseDataObject[])
      : filteredItems;
    const startIndex = pagination ? (pagination.page - 1) * pagination.pageSize : 0;
    const endIndex = pagination ? startIndex + pagination.pageSize : undefined;
    return pagination ? sortedItems.slice(startIndex, endIndex) : sortedItems;
  }, [sorts, filters, pagination, tagProps.type, selector]);

  const childrenContext = useMemo(
    () => ({
      ...context,
      collection: { items, setSorts, setFilters, setPagination },
    }),
    [context, items, setSorts, setFilters, setPagination]
  );

  if (!result.isValid) {
    console.error(
      'Тэг: $CollectionContext;',
      `Путь: ${context.path?.join('.') || 'Не указан'};`,
      result.errors
    );
    return <UniversalChildren context={context}>{tagProps.children}</UniversalChildren>;
  }

  return <UniversalChildren context={childrenContext}>{tagProps.children}</UniversalChildren>;
});
CustomCollection.displayName = 'CustomCollection';
